import type { DayTotals, IncentiveRow } from "./IncentiveRow";
import type { SixZoneThresholds } from "../constants/colors";

export const INCENTIVE_ROTATION_STATUS = {
	AVAILABLE: "available",
	NO_ROTATION: "noRotation",
	INSUFFICIENT_DATA: "insufficientData",
} as const;

export type IncentiveRotationStatus =
	(typeof INCENTIVE_ROTATION_STATUS)[keyof typeof INCENTIVE_ROTATION_STATUS];

export interface IncentiveRotationNavigation {
	status: IncentiveRotationStatus;
	selectedProductionDate: string;
	currentBlockStart: string | null;
	previousAnchor: string | null;
	nextAnchor: string | null;
	canNavigatePrevious: boolean;
	canNavigateNext: boolean;
}

/** Maps to RS3 of M2.GetIncentiveByWeek */
export interface CrewWeeklySummary {
	Crew: string;
	WeeklyFinished: number | null;
	WeeklyTarget: number | null;
	WeeklyIncPercent: number | null;
}

/** Maps to RS4 of M2.GetIncentiveByWeek */
export interface CrewPairSummary {
	CrewPair: string; // "AB" or "CD"
	CombinedFinished: number | null;
	CombinedTarget: number | null;
	CombinedIncPercent: number | null;
}

/** Maps to RS5 of M2.GetIncentiveByWeek — raw tank data from SP */
export interface TankRawData {
	TankKey: string; // "current" | "last" | "weekAB" | "weekCD"
	ShiftId: number | null;
	ProductionDate: string | null;
	Shift: string | null; // "D" or "N"
	Crew: string | null;
	Finished: number | null;
	Target: number | null;
	IncPercent: number | null;
}

/** Frontend presentation model for tank display */
export interface TankData {
	tankKey: string;
	label: string;
	sublabel: string;
	finished: number;
	target: number;
	incPercent: number | null;
}

/** Parsed from RS7 metadata row (RowType='META') */
export interface RotationMeta {
	currentBlockStart: string; // ISO date
	currentBlockEnd: string;
	currentCrewPair: "AB" | "CD";
	prevBlockStart: string;
	prevBlockEnd: string;
	prevCrewPair: "AB" | "CD";
}

/** Pivot row built by the frontend from RS1 data */
export interface CrewPivotRow {
	date: string;
	crewA: number | null;
	crewB: number | null;
	crewC: number | null;
	crewD: number | null;
	totalAB: number | null;
	totalCD: number | null;
	isTotal: boolean;
	/** Crew/aggregate keys whose value is 0 because the shift is future (not a real zero). */
	futureKeys: Set<string>;
	/** 0 = previous rotation block, 1 = current/dominant block. Undefined for total rows. */
	blockId?: number;
}

export interface WeeklyIncentiveData {
	dailyRows: IncentiveRow[];
	dayTotals: DayTotals[];
	crewWeekly: CrewWeeklySummary[];
	crewPairs: CrewPairSummary[];
	tanks: TankData[];
	thresholds: SixZoneThresholds;
	crewPivot: CrewPivotRow[];
	/** Rotation block metadata from RS7. Null when RS7 is absent (backward compat). */
	rotationMeta: RotationMeta | null;
	rotationNavigation: IncentiveRotationNavigation;
}
